/**
 * Main Navigation Component
 * Renders grouped, collapsible navigation sidebar with hover-expand behavior
 * FEA-125: Added user indicator showing current user and role
 * FEA-133: Shows other active users
 * FEA-212: Redesigned with 6-phase FMEA workflow navigation
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProjectSwitcher } from '../ProjectSwitcher/ProjectSwitcher';
import { NavGroup } from './NavGroup';
import { NavigationPhase } from './NavigationPhase';
import { getNavigationPhases, getAdditionalNavGroups } from './navigationConfig';
import { useNavigationState } from '../../hooks/useNavigationState';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveUsers } from '../../hooks/useActiveUsers';
import { useTerminology } from '@/hooks/useTerminology';
import './Navigation.css';

export const Navigation: React.FC = () => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const { isGroupExpanded, toggleGroup, activeGroupId } = useNavigationState();
  const { user, isAdmin, isLocalAdmin, isLoading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTerminology();

  // FEA-133: Track active users
  const { otherUsers, getInitials, getUserColor } = useActiveUsers();

  // FEA-212: Generate 6-phase navigation and additional groups
  const navigationPhases = useMemo(() => getNavigationPhases(t), [t]);
  const additionalGroups = useMemo(() => getAdditionalNavGroups(t, { isAdmin }), [t, isAdmin]);

  // FEA-216: Calculate active phase number for snake progress
  const activePhaseNumber = useMemo(() => {
    const currentPath = location.pathname;
    for (const phase of navigationPhases) {
      if (phase.items.some((item) => item.path === currentPath)) {
        return phase.number;
      }
    }
    return 0; // No phase active
  }, [location.pathname, navigationPhases]);

  // Role badge color
  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'role-admin';
      case 'PROJECT_MANAGER': return 'role-project-manager';
      case 'MANAGER': return 'role-manager';
      case 'ENGINEER': return 'role-engineer';
      case 'VIEWER': return 'role-viewer';
      case 'GUEST': return 'role-guest';
      default: return '';
    }
  };

  return (
    <nav
      className={`main-navigation ${isSidebarExpanded ? 'expanded' : ''}`}
      role="navigation"
      aria-label="Main navigation"
      onMouseEnter={() => { if (window.matchMedia('(hover: hover)').matches) setIsSidebarExpanded(true) }}
      onMouseLeave={() => { if (window.matchMedia('(hover: hover)').matches) setIsSidebarExpanded(false) }}
    >
      <div className="nav-container">
        <div className="nav-brand">
          <span className="nav-brand-icon" aria-hidden="true">
            ⚡
          </span>
          {isSidebarExpanded && (
            <div className="nav-brand-text">
              <h1>FMEA Studio</h1>
              <span className="nav-subtitle">{t.modeDescription}</span>
            </div>
          )}
        </div>

        {isSidebarExpanded && <ProjectSwitcher />}

        {/* FEA-212: Workflow phases, FEA-216: Progress snake container */}
        <ul className="nav-phases" role="menu" aria-label="Workflow phases" data-testid="nav-snake">
          {navigationPhases.map((phase) => (
            <NavigationPhase
              key={phase.id}
              phase={phase}
              isExpanded={isGroupExpanded(phase.id)}
              isSidebarExpanded={isSidebarExpanded}
              onToggle={() => toggleGroup(phase.id)}
              currentPath={location.pathname}
              activePhaseNumber={activePhaseNumber}
            />
          ))}
        </ul>

        {/* Separator — divides the primary workflow path from secondary utilities */}
        {isSidebarExpanded && <div className="nav-separator" />}
        {isSidebarExpanded && (
          <span className="nav-section-label" aria-hidden="true">Tools &amp; Settings</span>
        )}

        {/* Additional cross-cutting groups (Data & Admin) — visually secondary tier */}
        <ul className="nav-links nav-links--secondary" role="menu" aria-label="Additional sections">
          {additionalGroups.map((group) => (
            <NavGroup
              key={group.id}
              group={group}
              isExpanded={isGroupExpanded(group.id)}
              isSidebarExpanded={isSidebarExpanded}
              onToggle={() => toggleGroup(group.id)}
              activeGroupId={activeGroupId}
            />
          ))}
        </ul>

        {/* FEA-133: Other active users - clickable to navigate to their page */}
        {otherUsers.length > 0 && (
          <div className="nav-other-users">
            {isSidebarExpanded && (
              <span className="other-users-label">Also online:</span>
            )}
            <div className="other-users-avatars">
              {otherUsers.slice(0, 5).map((otherUser) => (
                <div
                  key={otherUser.userId}
                  className="other-user-avatar clickable"
                  style={{ backgroundColor: getUserColor(otherUser.userId) }}
                  title={`${otherUser.userName} - ${otherUser.currentPageName}\nClick to go there`}
                  onClick={() => navigate(otherUser.currentPath)}
                >
                  {getInitials(otherUser.userName)}
                </div>
              ))}
              {otherUsers.length > 5 && (
                <div className="other-user-avatar more-users" title={`${otherUsers.length - 5} more users`}>
                  +{otherUsers.length - 5}
                </div>
              )}
            </div>
            {isSidebarExpanded && otherUsers.length > 0 && (
              <div className="other-users-pages">
                {otherUsers.slice(0, 3).map((otherUser) => (
                  <div
                    key={otherUser.userId}
                    className="other-user-page"
                    onClick={() => navigate(otherUser.currentPath)}
                  >
                    <span className="other-user-page-name">{otherUser.userName.split(' ')[0]}:</span>
                    <span className="other-user-page-location">{otherUser.currentPageName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* FEA-125: User indicator showing current user and role */}
        {!authLoading && user && (
          <div className="nav-user-indicator" title={`${user.email} (${user.role})`}>
            <div className="user-avatar">
              {isLocalAdmin ? '👑' : user.name.charAt(0).toUpperCase()}
            </div>
            {isSidebarExpanded && (
              <div className="user-info">
                <span className="user-name">{isLocalAdmin ? 'Local Admin' : user.name}</span>
                <span className={`user-role ${getRoleBadgeClass(user.role)}`}>
                  {user.role}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Logout button — clears FMEA token and redirects to AUTH login */}
        {!authLoading && user && (
          <button
            onClick={logout}
            title="Sign out"
            className="nav-logout"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {isSidebarExpanded && <span>Logout</span>}
          </button>
        )}
      </div>
    </nav>
  );
};
